'use client';

import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useJobs } from '@gate-js/react';
import JobFilterFieldSkeleton from '../../jobs/JobFilterFieldSkeleton';

function getFieldLabels(value) {
	if (!value) return [];
	if (typeof value === 'string') return [value];
	if (Array.isArray(value)) return value.map((v) => v.label);
	return [value.label];
}

function JobFilterField({ items, label, onChange, value, color, jobs, fieldName }) {
	const [mounted, setMounted] = useState(false);
	const [selectedValue, setSelectedValue] = useState(value || []);
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (items) setMounted(true);
	}, [items]);

	useEffect(() => {
		setSelectedValue(value || []);
	}, [value]);

	const handleChange = (newValue) => {
		setSelectedValue(newValue);
		setQuery('');
		onChange(newValue);
	};

	const filteredItems = query === ''
		? items
		: items.filter((item) => item
			.toLowerCase()
			.replace(/\s+/g, '')
			.includes(query.toLowerCase().replace(/\s+/g, '')));

	// Show loading state during hydration
	if (!mounted) {
		return (
			<JobFilterFieldSkeleton label={label} color={{ color }} />
		);
	}

	return (
		<div className="group">
			{label && (
				<div
					style={{ color }}
					className="block text-sm font-semibold text-gray-700 mb-2 group-hover:text-gray-900 transition-colors duration-200"
				>
					{label}
				</div>
			)}
			<div className={label ? 'mt-2' : ''}>
				<Combobox value={selectedValue} onChange={handleChange} onClose={() => setQuery('')} multiple>
					<div className="relative">
						<div className="relative">
							<ComboboxInput
								className="group-hover:shadow-xl group-hover:shadow-gray-200/60 w-full min-w-lg cursor-pointer rounded-xl border-0 bg-white/95 backdrop-blur-sm py-2 pl-4 pr-12 text-gray-900 font-medium shadow-lg shadow-gray-200/50 ring-1 ring-gray-200 hover:ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:shadow-xl focus:shadow-blue-100/50 @sm:text-sm @sm:leading-6 transition-all duration-300 ease-out hover:bg-white placeholder:text-gray-400"
								displayValue={(vals) => (query || vals?.join(', ') || '')}
								onChange={(event) => setQuery(event.target.value)}
							/>
							<div className="absolute inset-y-0 right-0 flex items-center">
								{selectedValue.length > 0 && (
									<button
										type="button"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleChange([]);
										}}
										className="flex items-center justify-center w-6 h-6 mr-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all duration-200"
									>
										<svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
											<path
												d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
											/>
										</svg>
									</button>
								)}
								<ComboboxButton className="flex items-center pr-4">
									<svg
										className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-all duration-300 ease-out group-hover:scale-110"
										viewBox="0 0 20 20"
										fill="currentColor"
										aria-hidden="true"
									>
										<path
											fillRule="evenodd"
											  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
											  clipRule="evenodd"
										/>
									</svg>
								</ComboboxButton>
							</div>
						</div>

						<Transition
							as={Fragment}
							enter="transition ease-out duration-200"
							enterFrom="transform opacity-0 scale-95"
							enterTo="transform opacity-100 scale-100"
							leave="transition ease-in duration-150"
							leaveFrom="transform opacity-100 scale-100"
							leaveTo="transform opacity-0 scale-95"
							afterLeave={() => setQuery('')}
						>
							<ComboboxOptions
								className="absolute z-50 mt-2 max-h-60 w-full min-w-lg overflow-auto rounded-xl bg-white/95 backdrop-blur-sm py-2 text-base shadow-xl shadow-gray-300/50 ring-1 ring-gray-200 focus:outline-none @sm:text-sm"
							>
								{filteredItems.length === 0 && query !== '' ? (
									<div className="relative cursor-default select-none py-3 px-4 text-gray-500">
										-
									</div>
								) : (
									filteredItems.map((item) => {
										const count = jobs && fieldName
											? jobs.filter((job) => getFieldLabels(job.fields[fieldName]).includes(item)).length
											: null;
										return (
										<ComboboxOption
											key={item}
											className={({ active, selected }) => `relative cursor-pointer select-none py-3 pl-4 pr-12 transition-colors duration-150 ${
												active
													? 'bg-blue-50 text-blue-900'
													: selected
														? 'bg-gray-50 text-gray-900'
														: 'text-gray-700 hover:bg-gray-50'
											}`}
											value={item}
										>
											{({ selected, active }) => (
												<>
													<span
														className={`flex items-center gap-1.5 truncate font-medium ${selected ? 'font-semibold' : 'font-normal'}`}
													>
														{item || '-'}
														{count !== null && (
															<span className="text-xs text-gray-400 font-normal">({count})</span>
														)}
													</span>
													{selected && (
														<span
															className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
																active ? 'text-blue-600' : 'text-blue-500'
															}`}
														>
															<svg
																className="h-5 w-5"
																viewBox="0 0 20 20"
																 fill="currentColor"
																aria-hidden="true"
															>
																<path
																	fillRule="evenodd"
																	  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
																	  clipRule="evenodd"
																/>
															</svg>
														</span>
													)}
												</>
											)}
										</ComboboxOption>
									); })
								)}
							</ComboboxOptions>
						</Transition>
					</div>
				</Combobox>
			</div>
		</div>
	);
}

export function JobFilter({ filterBy, fields, color, className, style }) {
	const { setFilter, filter = {}, jobs } = useJobs();
	const allJobsRef = useRef(null);
	if (jobs && !allJobsRef.current) allJobsRef.current = jobs;

	const handleFilterChange = (fieldName, value) => {
		const newCustom = { ...filter.custom };

		if (!value || value.length === 0) {
			delete newCustom[fieldName];
		} else {
			newCustom[fieldName] = value;
		}

		setFilter({ ...filter, custom: newCustom });
	};

	return (
		fields && fields.length > 0 ? (
			<div className={classNames('grid grid-cols-2 md:grid-cols-3 lg:flex gap-4 px-2 py-3', className)} style={style}>
				{fields.filter((i) => filterBy.includes(i.name)).map((field) => (
					<JobFilterField
						key={field.name}
						label={field.label}
						items={field.options}
						color={color}
						value={filter.custom?.[field.name]}
						onChange={(value) => handleFilterChange(field.name, value)}
						jobs={allJobsRef.current}
						fieldName={field.name}
					/>
				))}
			</div>
		) : null
	);
}
